/* eslint-disable jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */

import React, { useState, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import { NextSeo } from 'next-seo'
import { useRouter } from 'next/router'
import {
  InstantSearch, connectHits, connectStats, connectSearchBox, connectStateResults,
} from 'react-instantsearch-dom'
import { isEmpty, compose } from 'ramda'
import Container from '@material-ui/core/Container'
import Grid from '@material-ui/core/Grid'
import GroupEventsSearchResults from '@components/group-events-search/GroupEventsResults/GroupEventsSearchResult'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import defaults from '@config/defaults'
import { makeGaFriendlyObj } from '@components/group-events-search/GroupEventsResults/utils'
import useDebounce from '@hooks/useDebounce'
import PlacesRefiner from '@components/group-events-search/GroupEventsResults/Refiners/PlacesRefiner'
import Refiners from '@components/group-events-search/GroupEventsResults/Refiners'
import MapViewResults from '@components/map-view-results'
import { gtmEvent } from '@lib/gtm-utils'
import ConfigureWithLocation from './ConfigureWithLocation'
import { useStyles } from './search.styles'

const resultsStringFor = (term, place) => {
  let resultString = ''
  if (term && place) {
    resultString = `Results for "${term}" near "${place}"`
  } else if (term) {
    resultString = `Results for "${term}"`
  } else if (place) {
    resultString = `Results near "${place}"`
  }
  return resultString
}

const isCompletePlace = placeName => placeName.includes(',')

const SearchResults = (props) => {
  const {
    currentRefinement,
    refine,
    hits,
    setAroundLatLng,
    setAroundRadius,
    aroundRadius,
    placeName,
    searchState,
    nbHits,
    allSearchResults,
  } = props

  const isMobile = useMediaQuery('(max-width:959.9px)')
  const [loadingLocationGtm, setLoadingLocationGtm] = useState(false)
  const [placeSearchValue, setPlaceSearchValue] = useState(placeName || '')
  const [showMapView, setShowMapView] = useState(false)
  const [showRefiners, setShowRefiners] = useState(false)

  const router = useRouter()

  const classes = useStyles({ showRefiners })

  const debouncedPlaceName = useDebounce(placeSearchValue, 1500)
  const debouncedsearchState = useDebounce(searchState, 1500)
  const [selectedOnMap, setSelectedOnMap] = useState([])
  const [cardHoveredHit, setCardHoveredHit] = useState()

  useEffect(() => {
    if (placeName) {
      const dataLayer = window.dataLayer || []
      dataLayer.push({
        event: 'groupEventsSearchLocation',
        searchLocationValue: placeName,
      })
      setPlaceSearchValue(placeName)
    }
  }, [])

  const firstRender = useRef(true)

  useEffect(() => {
    if (!isEmpty(debouncedsearchState)
      || (isCompletePlace(debouncedPlaceName) && !loadingLocationGtm)) {
      // Prevents events from firing on initial render before the debounce takes effect.
      if (firstRender.current) {
        firstRender.current = false
        return
      }

      const searchWithPlace = {
        ...debouncedsearchState,
        ...(isCompletePlace(debouncedPlaceName) && { placeName: debouncedPlaceName }),
      }
      const searchObj = makeGaFriendlyObj(searchWithPlace)

      gtmEvent({
        event: 'eventSearchQuery',
        attributes: { eventSearchQueryHits: nbHits, ...searchObj },
      })
    }
  }, [debouncedsearchState, debouncedPlaceName])

  const metaTitle = `${placeName ? `Group Events in ${placeName}` : 'Group Events Near Me'} - PGA of America`
  const metaDescription = 'Get golf lessons from a certified PGA coach who will provide golf instruction to players of all ages and abilities.'

  return (
    <Container className={classes.root} maxWidth={false}>
      <NextSeo
        noindex={defaults.noindexNofollow}
        nofollow={defaults.noindexNofollow}
        title={metaTitle}
        description={metaDescription}
        {...(router ? { canonical: router.asPath } : {})}
      />
      <Grid container spacing={4} justifyContent="flex-start" className={classes.searchContent}>
        <Grid item xs={12} md={6} lg={5}>
          <PlacesRefiner {...{
            aroundRadius,
            setAroundRadius,
            setAroundLatLng,
            placeSearchValue,
            setPlaceSearchValue,
            setLoadingLocationGtm,
            showMapView,
            setShowMapView,
            showRefiners,
            setShowRefiners,
          }}
          />
          <Refiners {...{
            aroundRadius,
            setAroundRadius,
            showRefiners,
            isMobile,
          }}
          />
          {(!isMobile || (isMobile && !showMapView)) && (
            <GroupEventsSearchResults {...{
              hits,
              currentRefinement,
              refine,
              resultsString: resultsStringFor(currentRefinement, placeSearchValue),
              placeSearchValue,
              setCardHoveredHit,
              selectedOnMap,
              showRefiners,
            }}
            />
          )}
        </Grid>
        {((!isMobile) || (isMobile && showMapView)) && (
          <Grid item xs={12} md={6} lg={7} className={classes.mapViewMapContainer}>
            {hits.length >= 0 && (
              <MapViewResults
                hits={hits}
                cardHoveredHit={cardHoveredHit}
                setSelected={setSelectedOnMap}
                selectedHit={selectedOnMap}
                searchState={searchState}
                aroundRadius={aroundRadius}
                aroundIpLatLng={allSearchResults.aroundLatLng}
              />
            )}
          </Grid>
        )}
      </Grid>
    </Container>
  )
}

SearchResults.propTypes = {
  currentRefinement: PropTypes.string,
  refine: PropTypes.func,
  hits: PropTypes.array,
  setAroundLatLng: PropTypes.func,
  setAroundRadius: PropTypes.func,
  aroundRadius: PropTypes.number,
  placeName: PropTypes.string,
  nbHits: PropTypes.number,
  searchState: PropTypes.object,
  allSearchResults: PropTypes.object,
}

const SearchBox = compose(
  connectStats,
  connectHits,
  connectSearchBox,
  connectStateResults,
)(SearchResults)

const GroupEventsSearchLayout = (props) => {
  const {
    searchClient,
    resultsState,
    onSearchStateChange,
    searchState,
    createURL,
    indexName,
    onSearchParameters,
    aroundLatLng,
    aroundRadius,
    setAroundLatLng,
    setAroundRadius,
    placeName,
  } = props
  return (
    <InstantSearch
      searchClient={searchClient}
      resultsState={resultsState}
      onSearchStateChange={onSearchStateChange}
      searchState={searchState}
      createURL={createURL}
      indexName={indexName}
      onSearchParameters={onSearchParameters}
      {...props}
    >
      <ConfigureWithLocation {...{ aroundLatLng, aroundRadius, searchState }} />
      <SearchBox {...{
        searchState,
        setAroundLatLng,
        setAroundRadius,
        aroundRadius,
        placeName,
      }}
      />
    </InstantSearch>
  )
}

GroupEventsSearchLayout.propTypes = {
  searchClient: PropTypes.object,
  resultsState: PropTypes.object,
  onSearchStateChange: PropTypes.func,
  searchState: PropTypes.object,
  createURL: PropTypes.any,
  indexName: PropTypes.string,
  onSearchParameters: PropTypes.func,
  aroundLatLng: PropTypes.string,
  aroundRadius: PropTypes.number,
  setAroundLatLng: PropTypes.func,
  setAroundRadius: PropTypes.func,
  placeName: PropTypes.string,
}

const GroupEventsSearchFindResultsState = (props) => {
  const {
    searchClient,
    resultsState,
    onSearchStateChange,
    searchState,
    createURL,
    indexName,
    onSearchParameters,
    aroundLatLng,
    aroundRadius,
  } = props
  return (
    <InstantSearch
      searchClient={searchClient}
      resultsState={resultsState}
      onSearchStateChange={onSearchStateChange}
      searchState={searchState}
      createURL={createURL}
      indexName={indexName}
      onSearchParameters={onSearchParameters}
      {...props}
    >
      <ConfigureWithLocation {...{ aroundLatLng, aroundRadius }} />
      {/* when pre-populating search results using findResultsState, we need
        * to include any refiners that will be rendered in the actual layout,
        * otherwise refinement options coming from the query string are ignored
        * - ref: https://github.com/algolia/react-instantsearch/issues/1982#issuecomment-460295364
        */}
    </InstantSearch>
  )
}

GroupEventsSearchFindResultsState.propTypes = {
  searchClient: PropTypes.object,
  resultsState: PropTypes.object,
  onSearchStateChange: PropTypes.func,
  searchState: PropTypes.object,
  createURL: PropTypes.any,
  indexName: PropTypes.string,
  onSearchParameters: PropTypes.func,
  aroundLatLng: PropTypes.string,
  aroundRadius: PropTypes.number,
}

export { GroupEventsSearchFindResultsState }

export default GroupEventsSearchLayout
